/**
 * Pilotage geometry — clearing bearings, the first real pilotage tool.
 *
 * A clearing bearing is a SAFETY BOUNDARY anchored on a charted object. The
 * navigator sights the object with a hand-bearing compass, so the bearing we
 * quote and label is the object's compass bearing FROM the vessel (°T). The
 * fixed end is pinned to the object; the free end is drawn on the vessel's side
 * and dragged to set the bearing, so the label always reads the bearing you'd
 * see the object on. Stay on the safe side and you're clear; cross to the other
 * side and the cross alarm fires.
 */
import { rhumbBearingDeg, rhumbDestination, rhumbDistanceNm, formatBearing, transitToLine } from '@/nav';

export interface ClearingBearing {
  id: string;
  name?: string;
  /** The charted object's position [lon, lat] — the fixed end. */
  anchor: [number, number];
  /** The object's name or type, for the label and the lists. */
  anchorLabel?: string;
  /** The clearing bearing: the object's compass bearing FROM the vessel, °T. */
  bearingDeg: number;
  /** Drawn length from the anchor out to the free-end handle, nm. */
  lengthNm: number;
  /** Which side of the line (looking along the bearing, past the object) is safe. */
  safeSide: 'left' | 'right';
  /** Whether this clearing actively guards (raises the cross alarm). */
  alarm: boolean;
  /** The pilotage plan it belongs to, or null for a one-off. */
  planId: string | null;
}

export interface PilotagePlan {
  id: string;
  name: string;
}

const recip = (deg: number): number => (deg + 180) % 360;

/** The free-end handle, on the vessel side (the object bears `bearingDeg` from here). */
export function clearingFreeEnd(cb: ClearingBearing): [number, number] {
  return rhumbDestination(cb.anchor, recip(cb.bearingDeg), Math.max(0.05, cb.lengthNm));
}

/** A short extension past the object, so the boundary reads as a full line. */
export function clearingFarEnd(cb: ClearingBearing, extendNm = 0.35): [number, number] {
  return rhumbDestination(cb.anchor, cb.bearingDeg, extendNm);
}

/** Local east/north offset (nm) of `to` from `from`. Flat-earth, fine at this scale. */
function offsetEN(from: [number, number], to: [number, number]): [number, number] {
  const east = (to[0] - from[0]) * 60 * Math.cos((from[1] * Math.PI) / 180);
  const north = (to[1] - from[1]) * 60;
  return [east, north];
}

/**
 * Which side of the clearing line a point lies on, looking along the bearing
 * (from the vessel, on past the object). 'on' when within a whisker of the line.
 */
export function clearingSide(cb: ClearingBearing, p: [number, number]): 'left' | 'right' | 'on' {
  const b = (cb.bearingDeg * Math.PI) / 180;
  const dirE = Math.sin(b);
  const dirN = Math.cos(b);
  const [vE, vN] = offsetEN(cb.anchor, p);
  const z = dirE * vN - dirN * vE; // 2D cross product: sign gives the side
  if (Math.abs(z) < 1e-6) return 'on';
  return z > 0 ? 'left' : 'right';
}

/** True when the vessel is on the unsafe side of a guarding clearing bearing. */
export function isClearingTripped(cb: ClearingBearing, vessel: [number, number] | null): boolean {
  if (!vessel) return false;
  const side = clearingSide(cb, vessel);
  return side !== 'on' && side !== cb.safeSide;
}

/** The bearing + length to store when the free end is dragged to `p`. */
export function clearingDragUpdate(
  anchor: [number, number],
  p: [number, number],
): { bearingDeg: number; lengthNm: number } {
  return {
    bearingDeg: rhumbBearingDeg(p, anchor),
    lengthNm: Math.max(0.05, rhumbDistanceNm(anchor, p)),
  };
}

/** A friendly label, e.g. "Fairway buoy 047°T", or the typed name. */
export function clearingDisplayName(cb: ClearingBearing): string {
  if (cb.name && cb.name.trim()) return cb.name.trim();
  const brg = formatBearing(cb.bearingDeg);
  return cb.anchorLabel ? `${cb.anchorLabel} ${brg}` : `Clearing ${brg}`;
}

// -----------------------------------------------------------------------------
// Transits — two charted objects in line (a leading line you follow, not a
// boundary you guard). Keyed to the two objects' charted positions.
// -----------------------------------------------------------------------------

export interface Transit {
  id: string;
  name?: string;
  /** First object's charted position [lon, lat]. */
  a: [number, number];
  /** Second object's charted position [lon, lat]. */
  b: [number, number];
  aLabel?: string;
  bLabel?: string;
  /** How far the leading line runs out PAST the front mark (b), nm. Draggable. */
  extendNm?: number;
  /** The pilotage plan it belongs to, or null for a one-off. */
  planId: string | null;
}

/**
 * Bearing of the transit line, the bearing of the two marks in line (°T), from
 * the first object to the second. Reuses the fix-builder's transit geometry
 * (transitToLine gives bearing rear→front; front=b, rear=a gives a→b).
 */
export function transitBearingDeg(t: Transit): number {
  return transitToLine(t.b, t.a).bearingDeg;
}

/**
 * A transit is DIRECTIONAL: the first mark (a) is the BACK (rear), the second
 * (b) is the FRONT, and the leading line you run in on extends PAST the front,
 * not behind the back. So it draws back -> front -> on out beyond the front.
 */
export const TRANSIT_DEFAULT_EXTEND_NM = 0.5;

/** How far the line runs past the front mark (nm); the stored value or default. */
export function transitExtendNm(t: Transit): number {
  return t.extendNm != null && t.extendNm > 0 ? t.extendNm : TRANSIT_DEFAULT_EXTEND_NM;
}

/** The far end of the leading line, out beyond the front mark (b). Drag handle. */
export function transitExtendedEnd(t: Transit): [number, number] {
  return rhumbDestination(t.b, transitBearingDeg(t), transitExtendNm(t));
}

/** The drawn polyline: back (a) -> front (b) -> extended out past the front. */
export function transitDrawLine(t: Transit): [number, number][] {
  return [t.a, t.b, transitExtendedEnd(t)];
}

/**
 * New extend length (nm) when the far-end handle is dragged to `p`: the distance
 * past the front mark measured ALONG the transit bearing, so the handle stays on
 * the leading line and just lengthens or shortens it. Clamped to a small min.
 */
export function transitExtendDragUpdate(t: Transit, p: [number, number]): number {
  const brg = (transitBearingDeg(t) * Math.PI) / 180;
  const dirE = Math.sin(brg);
  const dirN = Math.cos(brg);
  const east = (p[0] - t.b[0]) * 60 * Math.cos((t.b[1] * Math.PI) / 180);
  const north = (p[1] - t.b[1]) * 60;
  return Math.max(0.05, east * dirE + north * dirN);
}

/** A friendly label, e.g. "Church / Beacon 047°T", or the typed name. */
export function transitDisplayName(t: Transit): string {
  if (t.name && t.name.trim()) return t.name.trim();
  const brg = formatBearing(transitBearingDeg(t));
  if (t.aLabel && t.bLabel) return `${t.aLabel} / ${t.bLabel} ${brg}`;
  return `Transit ${brg}`;
}

// -----------------------------------------------------------------------------
// Heading / back bearings — a single bearing line on one charted object (or a
// tapped point), not a two-mark transit and not a safety boundary. A HEADING
// bearing is to an object AHEAD: the bearing you sight on it is the course you
// steer to close it. A BACK bearing is to an object ASTERN: the bearing you read
// looking back at it is the reciprocal of your course, so it confirms you're
// holding your track. Same object-from-vessel bearing underneath; the kind
// changes how we read it and which way the line points.
// -----------------------------------------------------------------------------

export interface HeadingBearing {
  id: string;
  name?: string;
  /** 'heading' = object ahead (steer toward); 'back' = object astern (look back). */
  kind: 'heading' | 'back';
  /** The charted object / tapped point we sight, the fixed end [lon, lat]. */
  anchor: [number, number];
  /** The object's name or type, for the label and the lists. */
  anchorLabel?: string;
  /** The vessel-side reference end [lon, lat], the draggable handle. */
  from: [number, number];
  /** The pilotage plan it belongs to, or null for a one-off. */
  planId: string | null;
}

/** How far the line runs out past its forward end, nm. */
export const HEADING_EXTEND_NM = 0.3;

/**
 * A sensible vessel-side end when we have no fix to anchor it to: ~0.8 nm due
 * south of the object, so the line is visible and ready to drag onto the real
 * bearing.
 */
export function headingDefaultFrom(anchor: [number, number]): [number, number] {
  return rhumbDestination(anchor, 180, 0.8);
}

/** The compass bearing of the object FROM the vessel end (°T), the sighted value. */
export function headingObjectBearingDeg(hb: HeadingBearing): number {
  return rhumbBearingDeg(hb.from, hb.anchor);
}

/**
 * The course this bearing means we steer (°T). For a heading bearing the object
 * is ahead, so we steer straight at it (the object's bearing). For a back
 * bearing the object is astern, so our course is the reciprocal of the bearing
 * we read looking back at it.
 */
export function headingSteerDeg(hb: HeadingBearing): number {
  const b = headingObjectBearingDeg(hb);
  return hb.kind === 'back' ? (b + 180) % 360 : b;
}

/**
 * The bearing we put on the label and read in the cockpit (°T): the object's
 * bearing from the vessel either way (you sight the object), because that's the
 * number you actually compare against the compass. For a heading bearing it's
 * also the course; for a back bearing the course is its reciprocal.
 */
export function headingLabelBearingDeg(hb: HeadingBearing): number {
  return headingObjectBearingDeg(hb);
}

/**
 * The drawn polyline. A heading bearing runs from the vessel, through the object
 * ahead, and a touch past it (we're heading toward and beyond it). A back
 * bearing runs from the object astern, up to the vessel, then a touch on past
 * the vessel along the course (the mark drops astern as we carry on). So the
 * forward end (where the arrow sits) is past the object for a heading bearing,
 * and ahead of the vessel for a back bearing.
 */
export function headingDrawLine(hb: HeadingBearing): [number, number][] {
  const objB = headingObjectBearingDeg(hb);
  if (hb.kind === 'back') {
    const ahead = rhumbDestination(hb.from, (objB + 180) % 360, HEADING_EXTEND_NM);
    return [hb.anchor, hb.from, ahead];
  }
  const past = rhumbDestination(hb.anchor, objB, HEADING_EXTEND_NM);
  return [hb.from, hb.anchor, past];
}

/** The forward end of the line, where the direction-of-travel arrow sits. */
export function headingForwardEnd(hb: HeadingBearing): [number, number] {
  const line = headingDrawLine(hb);
  return line[line.length - 1];
}

/** The direction the travel arrow points at the forward end (°T) = the course. */
export function headingArrowDeg(hb: HeadingBearing): number {
  return headingSteerDeg(hb);
}

/** A friendly label, e.g. "Beacon 047°T", or the typed name. */
export function headingDisplayName(hb: HeadingBearing): string {
  if (hb.name && hb.name.trim()) return hb.name.trim();
  const brg = formatBearing(headingLabelBearingDeg(hb));
  const what = hb.kind === 'back' ? 'Back brg' : 'Heading';
  return hb.anchorLabel ? `${hb.anchorLabel} ${brg}` : `${what} ${brg}`;
}

/**
 * The vessel-side end that makes the object sight on `labelDeg` (°T), keeping
 * the current object-to-vessel distance. The label bearing is the object seen
 * FROM the vessel, so the vessel sits on the reciprocal side of the object; we
 * place `from` at (labelDeg + 180) from the anchor. Lets the bearing be typed
 * in as well as dragged, which is what the tap-to-edit menu uses.
 */
export function headingFromForLabelBearing(hb: HeadingBearing, labelDeg: number): [number, number] {
  const d = Math.max(0.05, rhumbDistanceNm(hb.from, hb.anchor));
  const t = ((labelDeg % 360) + 360) % 360;
  return rhumbDestination(hb.anchor, (t + 180) % 360, d);
}

// -----------------------------------------------------------------------------
// Clearing depth — a "do not cross" depth contour. We pin it to a charted
// isobath's depth value; the same value is highlighted red on the chart and the
// alarm fires when the charted depth under the boat falls below it (i.e. we've
// moved onto water charted shallower than that contour). Like a clearing bearing
// but the boundary is a depth, not a line on a compass bearing.
// -----------------------------------------------------------------------------

export interface ClearingDepth {
  id: string;
  name?: string;
  /** The contour's charted depth value, metres — the do-not-cross isobath. */
  valueM: number;
  /** A point on/near the tapped contour, for the label and a small marker. */
  anchor: [number, number];
  /** Whether this clearing depth actively guards (raises the alarm). */
  alarm: boolean;
  /**
   * Which side of the contour is the danger, so which crossing fires the alarm.
   * 'shallow' (the default, and how every older saved one behaves) guards
   * against getting too shallow; 'deep' guards the other way, for a contour we
   * want to stay INSIDE of. Undefined reads as 'shallow'.
   */
  side?: 'shallow' | 'deep';
  /** The pilotage plan it belongs to, or null for a one-off. */
  planId: string | null;
}

/** Format a depth value plainly, e.g. "5 m" or "3.5 m". */
export function formatDepthM(m: number): string {
  return `${Number.isInteger(m) ? m : m.toFixed(1)} m`;
}

/** A friendly label, e.g. "5 m do-not-cross", or the typed name. */
export function clearingDepthDisplayName(cd: ClearingDepth): string {
  if (cd.name && cd.name.trim()) return cd.name.trim();
  return `${formatDepthM(cd.valueM)} do-not-cross`;
}

/**
 * True when the boat has crossed onto the guarded (danger) side of a clearing
 * depth. We compare the charted depth under the boat (the shallow end of the
 * depth area it sits in, or a nearby sounding) against the contour value.
 *
 * For a 'shallow'-side guard (the default) a STRICT less-than means the boat is
 * in water charted shallower than the contour, i.e. it has crossed to the
 * shallow side. Equal isn't a trip, because a depth area reports its shallow
 * end, so sitting on the deep side of the X m contour reads as exactly X and
 * must not fire.
 *
 * A 'deep'-side guard is the complement: reading X or more means the boat is on
 * the deep side of the X m contour (again because the area reports its shallow
 * end), so a value at-or-above the contour is the trip.
 */
export function isClearingDepthTripped(cd: ClearingDepth, chartedDepthM: number | null): boolean {
  if (chartedDepthM == null) return false;
  return cd.side === 'deep' ? chartedDepthM >= cd.valueM : chartedDepthM < cd.valueM;
}
